import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpJobChangeService from "@plannel/services/fp/fp-jobchange-service";
import ResourcePopup from "@plannel/pages/factory-plan/common/popup/ResourcePopup";
import RouteGroupPopup from "@plannel/pages/factory-plan/common/popup/RouteGroupPopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, DownloadButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// old validRules required 3개(resourceCd/prevRouteGrpCd/nextRouteGrpCd) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "resourceCd", label: "FP_RESOURCE_CD" },
  { field: "prevRouteGrpCd", label: "FP_PREV_ROUTE_GRP_CD" },
  { field: "nextRouteGrpCd", label: "FP_NEXT_ROUTE_GRP_CD" }
];

/**
 * 공정 그룹간 작업 교체(Route Group Job Change) 탭 — old RouteGroupJobChangeTab.jsx 대응.
 * RouteJobChangeTab.js와 동일 구성(전/후 공정그룹 동일코드 방지 검증 포함). old의
 * `ResourceRouteGroupPopup`(자원 기준 공정그룹 필터)은 공용 `RouteGroupPopup`(비필터,
 * 선택 모드)으로 대체 — TODO(MIG-T2) 참고.
 *
 * props: t, viewName, planScope, searchFields
 */
const RouteGroupJobChangeTab = ({ t, viewName, planScope, searchFields }) => {
  const gridRef = useRef();
  const [routeGroupJobChange, setRouteGroupJobChange] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [resourcePopup, setResourcePopup] = useState({ open: false, node: null });
  const [routeGroupPopup, setRouteGroupPopup] = useState({ open: false, node: null, target: "" });

  const divideTpValuesRef = useCommonCodeValues("FP_DIVIDE_TP_CD");
  const timeUomValuesRef = useCommonCodeValues("FP_TIME_UOM");
  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_ROUTE_GROUP_JOB_CHANGE"), viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);

  // rowData는 절대 새 배열로 갈아치우지 않는다(§6-1 — getRowId 없는 상태에서 rowData 참조가 바뀌면
  // grid row model이 재생성되어 미저장 신규 행이 조회조건 변경 시 사라지는 버그가 있었다).
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { resourceCdNm, prevRouteGrpCd, nextRouteGrpCd } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (resourceCdNm && !(hit(r.resourceCd, resourceCdNm) || hit(r.resourceNm, resourceCdNm))) return false;
    if (prevRouteGrpCd && !hit(r.prevRouteGrpCd, prevRouteGrpCd)) return false;
    if (nextRouteGrpCd && !hit(r.nextRouteGrpCd, nextRouteGrpCd)) return false;
    return true;
  };

  const isExternalFilterPresent = () => Object.values(searchFields).some(v => !!v);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  const openRouteGroupPopup = (node, target) => {
    if (!node.data?.resourceCd) return;
    setRouteGroupPopup({ open: true, node, target });
  };

  // TODO(MIG-T2): [jobchangetime/RouteGroupJobChangeTab] 자원 기준 공정그룹 목록 필터(old
  //   ResourceRouteGroupPopup) 미이관.
  //   old: ResourceRouteGroupPopup이 resourceCd prop을 받아 서버에서 해당 자원의 공정그룹만 조회.
  //   사유: 공용 RouteGroupPopup은 자원 필터를 지원하지 않음(전량 조회) — 1차 목표에는 지장 없음.
  const columnDefs = [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // old buttonVisibleCallback: isNewGridButtonVisible 대응 — 신규 행에서만 재선택 가능(§3-3).
    { headerName: "FP_RESOURCE_CD", field: "resourceCd", colId: "resourceCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => setResourcePopup({ open: true, node }), hideButtonWhenSaved: true } },
    { headerName: "FP_RESOURCE_NM", field: "resourceNm", colId: "resourceNm", type: ["nonEditableColumn"], width: 240 },
    // old buttonVisibleCallback: isGridResourceButtonVisible 대응 — resourceCd 선택 후 + 신규 행에서만(§3-4).
    { headerName: "FP_PREV_ROUTE_GRP_CD", field: "prevRouteGrpCd", colId: "prevRouteGrpCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => openRouteGroupPopup(node, "prevRouteGrpCd"), hideButtonWhenSaved: true, requiredField: "resourceCd" } },
    { headerName: "FP_PREV_ROUTE_GRP_NM", field: "prevRouteGrpNm", colId: "prevRouteGrpNm", type: ["nonEditableColumn"], width: 240 },
    { headerName: "FP_NEXT_JC_TM", field: "nextJcTime", colId: "nextJcTime", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_PREV_JC_TM", field: "prevJcTime", colId: "prevJcTime", width: 110, type: ["numberColumn", "rightAligned"] },
    // old buttonVisibleCallback: isGridResourceButtonVisible 대응 — resourceCd 선택 후 + 신규 행에서만(§3-4).
    { headerName: "FP_NEXT_ROUTE_GRP_CD", field: "nextRouteGrpCd", colId: "nextRouteGrpCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => openRouteGroupPopup(node, "nextRouteGrpCd"), hideButtonWhenSaved: true, requiredField: "resourceCd" } },
    { headerName: "FP_NEXT_ROUTE_GRP_NM", field: "nextRouteGrpNm", colId: "nextRouteGrpNm", type: ["nonEditableColumn"], width: 240 },
    { headerName: "FP_TIME_UOM", field: "timeUom", colId: "timeUom", width: 120, type: ["centerAligned"],
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => codeComboCellEditorParams("FP_TIME_UOM", timeUomValuesRef, t) },
    { headerName: "FP_JC_DIVIDE_TP_CD", field: "jcDivideTpCd", colId: "jcDivideTpCd", width: 110, type: ["centerAligned"],
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => codeComboCellEditorParams("FP_DIVIDE_TP_CD", divideTpValuesRef, t) },
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    }
  ];

  const retrieveRouteGroupJobChange = () => {
    if (!planScope) return;
    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    fpJobChangeService.getRouteGroupJobChanges({ planScope })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setRouteGroupJobChange([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setRouteGroupJobChange(results);
        }
      })
      .catch(e => debug(e))
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        DataState.initialize(gridRef.current?.api);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    retrieveRouteGroupJobChange();
  };

  const insertRow = () => {
    const newRow = {
      id: null, planScope, resourceCd: "", resourceNm: "",
      prevRouteGrpCd: "", prevRouteGrpNm: "", nextRouteGrpCd: "", nextRouteGrpNm: "",
      nextJcTime: 0, prevJcTime: 0, timeUom: "MINUTE", jcDivideTpCd: "Y"
    };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeRouteGroupJobChange = () => {
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpJobChangeService.removeRouteGroupJobChanges(selectedRows)
        .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
        .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }))
        .then(() => retrieveRouteGroupJobChange());
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveRouteGroupJobChange = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    fpJobChangeService.upsertRouteGroupJobChanges(allStateData)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch(e => { setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); debug(e); })
      .then(() => retrieveRouteGroupJobChange());
  };

  const onSelectResource = (row) => {
    const node = resourcePopup.node;
    if (node) {
      node.setDataValue("resourceCd", row.resourceCd);
      node.setDataValue("resourceNm", row.resourceNm);
      node.setDataValue("prevRouteGrpCd", "");
      node.setDataValue("prevRouteGrpNm", "");
      node.setDataValue("nextRouteGrpCd", "");
      node.setDataValue("nextRouteGrpNm", "");
    }
  };

  const onSelectRouteGroup = (row) => {
    const { node, target } = routeGroupPopup;
    if (!node) return;

    // old: 전/후 공정그룹에 같은 그룹을 중복 선택하지 못하게 막는 검증.
    const other = target === "prevRouteGrpCd" ? node.data?.nextRouteGrpCd : node.data?.prevRouteGrpCd;
    if (row.routeGrpCd === other) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_FP_NOT_SAME_ROUTE_GRP") });
      return;
    }

    if (target === "prevRouteGrpCd") {
      node.setDataValue("prevRouteGrpCd", row.routeGrpCd);
      node.setDataValue("prevRouteGrpNm", row.routeGrpNm);
    } else if (target === "nextRouteGrpCd") {
      node.setDataValue("nextRouteGrpCd", row.routeGrpCd);
      node.setDataValue("nextRouteGrpNm", row.routeGrpNm);
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveRouteGroupJobChange();
    else if (action === "DELETE") removeRouteGroupJobChange();
  };

  useEffect(() => {
    if (!gridRef.current?.api) return;
    retrieveRouteGroupJobChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <DownloadButton onClick={() => gridRef.current.api.exportDataAsExcel({ fileName: `${t("FP_ROUTE_GROUP_JOB_CHANGE")}.xlsx`, sheetName: t("FP_ROUTE_GROUP_JOB_CHANGE") })} />
        <AddButton onClick={insertRow} />
        <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
        <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
      </Box>
      <Box id="fp-route-grp-jc-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={routeGroupJobChange}
          {...defaultGridMemo}
          rowSelection={{
            ...defaultGridMemo.rowSelection,
            headerCheckbox: true,
            checkboxes: true
          }}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onGridReady={onGridReady}
        />
      </Box>
      <ResourcePopup
        open={resourcePopup.open}
        onClose={() => setResourcePopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectResource}
      />
      <RouteGroupPopup
        open={routeGroupPopup.open}
        onClose={() => setRouteGroupPopup({ open: false, node: null, target: "" })}
        planScope={planScope}
        onSelect={onSelectRouteGroup}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(RouteGroupJobChangeTab);
